import { useEffect, useState } from 'react'
import { Avatar, Button } from 'dsm-react-lib'
import { IconCheck, IconBookmark } from '@tabler/icons-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAppsService } from '../../_services/_index'
import styled from 'styled-components'
import { appInsights } from '../../_services/AppInsights'
import { EventType } from '../../utility/types'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { APP } from '../../configs/constants'
import CardTooltip from '../_molecules/CardTooltip'
import { ButtonSpinner } from '../../styles/common'
import { useSasToken } from '../../_hooks/useSasToken'

export const MediaContainer = styled.div`
  margin: 15px 0;
`
export const MediaDetailsContainer = styled.div`
  margin: 0 2rem;
`
export const ImageColumn = styled.div`
  width: ${(props) => (props.size === 'sm' ? '60px' : '74px')};
  height: ${(props) => (props.size === 'sm' ? '60px' : '74px')};
  margin-right: 1rem;
  position: relative;
`

export const ButtonColumn = styled.div`
  margin-left: 1rem;
`

export const AddBtn = styled(Button)`
  width: 110px;
`

export const TitleColumn = styled.div`
  width: ${(props) => (props.size === 'sm' ? '220px' : '100%')};
  @media (max-width: 1650px) {
    width: ${(props) => (props.size === 'sm' ? '300px' : '100%')};
  }
  @media (max-width: 1400px) {
    width: ${(props) => (props.size === 'sm' ? '220px' : '100%')};
  }
`

export const AppContainer = styled.div`
  max-width: 25vw;
  flex-wrap: wrap;
  @media (max-width: 1800px) {
    max-width: 20vw;
  }
  @media (max-width: 1650px) {
    max-width: 15vw;
  }
`

export const RelatedAppTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: var(--text-heading);
  text-align: left;
  overflow-wrap: break-word;
`
export const RelatedAppDescription = styled.div`
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 21px;
  text-align: left;
  vertical-align: top;
  opacity: 70%;
  color: var(--text-heading);
  font-weight: 400;
`

const MediaApp = ({
  item,
  size = 'md',
  isPageDetails = false,
  uniqueSectionName = '',
  from,
  name,
  id,
  appsInWorkspace
}) => {
  const navigate = useNavigate()
  const { appendToken } = useSasToken()
  const { addingApplication, addApplication, removingApplication, removeApplication } =
    useAppsService()
  const [foundAppInWorkspace, setFoundAppInWorkspace] = useState(false)

  useEffect(() => {
    const foundApp = appsInWorkspace?.find((workspaceApp) => workspaceApp.id === item.id)
    setFoundAppInWorkspace(!!foundApp)
  }, [appsInWorkspace])

  const itemMapper = {
    name: item !== undefined ? item.name : APP.NAME,
    description: item !== undefined ? item.shortDescription : APP.SHORT_DESCRIPTION,
    icon:
      item?.appIcon && Object.keys(item?.appIcon)?.length
        ? item?.appIcon?.url
        : item?.applicationIcon,
    id: item !== undefined ? item.appIconId : '#noID'
  }

  const trackAddToWorkspace = useTrackEvent(appInsights, EventType.AddToWorkspace)

  const addToWorkspace = async (item) => {
    trackAddToWorkspace({ appAdd: JSON.stringify(item) })
    const response = foundAppInWorkspace
      ? await removeApplication(item)
      : await addApplication(item)

    if (response?.status === 0) {
      setFoundAppInWorkspace((foundAppInWorkspace) => !foundAppInWorkspace)
    }
  }
  const incomingRequest = (item) => {
    if (!isPageDetails) return

    navigate({
      pathname: `/apps/${item.uniqueName}`,
      state: { from, name, id }
    })
    window.location.reload()
  }

  const getIcon = () => {
    if (foundAppInWorkspace) {
      return removingApplication ? (
        <ButtonSpinner color="white" />
      ) : (
        <IconCheck size={16} className="mr-1" />
      )
    } else {
      return addingApplication ? (
        <ButtonSpinner color="white" />
      ) : (
        <IconBookmark size={16} className="mr-1" />
      )
    }
  }

  return (
    <div className="d-flex flex-row align-items-center p-1" data-testid={`app-${item.uniqueName}`}>
      <ImageColumn size={size}>
        <Link
          to={{
            pathname: item.uniqueName !== undefined ? `/apps/${item.uniqueName}` : '/app-store',
            state: { from, name, id }
          }}
          onClick={() => incomingRequest(item)}
          data-testid={`favAppImg-${item.uniqueName}`}
          className="theme-text-heading">
          <Avatar
            data-tooltip-id={`media-app-${item.uniqueName}`}
            content={appendToken(itemMapper.icon)}
            size={size === 'sm' ? '60px' : '74px'}
            shape="rounded"
            alt={itemMapper.name}
            data-testid={`mediaApp-${item.uniqueName}`}
          />
          <CardTooltip id={`media-app-${item.uniqueName}`} item={item} />
        </Link>
      </ImageColumn>
      <TitleColumn size={size}>
        <AppContainer data-tip data-for={`app-tooltip-${item.uniqueName}`}>
          <Link
            to={{
              pathname: item.uniqueName !== undefined ? `/apps/${item.uniqueName}` : '/app-store',
              state: { from, name, id }
            }}
            data-testid={`mediaAppTitle-${itemMapper.id}`}
            onClick={() => incomingRequest(item)}>
            <RelatedAppTitle>{itemMapper.name}</RelatedAppTitle>
          </Link>
          <RelatedAppDescription>{itemMapper.description}</RelatedAppDescription>
        </AppContainer>
      </TitleColumn>
      <ButtonColumn size={size}>
        <AddBtn
          label={foundAppInWorkspace ? 'Added' : 'Add'}
          color={foundAppInWorkspace ? 'success' : 'primary'}
          variant={foundAppInWorkspace ? 'fill' : 'outline'}
          className={`${uniqueSectionName}-add-1 d-flex justify-content-center 
          ${foundAppInWorkspace ? '' : 'btn-custom-hover'} 
          btn-block`}
          id={`btn-item-${item.uniqueName}-${uniqueSectionName}`}
          onClick={() => addToWorkspace(item)}
          data-testid={`mediaAppBtn-${itemMapper.id}`}
          icon={getIcon()}
          iconOnly={false}
        />
      </ButtonColumn>
    </div>
  )
}
export default MediaApp
